import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ThemeToggle } from "../ui";
import { monogramOf, useTenantBranding, type TenantBrandingState } from "../auth/tenantBranding";

// Shared shell for the public auth surfaces — login, email-link request/redeem,
// OIDC callback (E1 US-1.1 / US-1.3). Rendered OUTSIDE the app shell.
//
// Two panes (Brändiehdotus v2, ref. Copy.ai sign-in): a coral→indigo Verso brand
// aside on wide screens beside the sign-in card. The card carries the tenant
// branding slot — the tenant's own LOGO and NAME, read from the unauthenticated
// `GET /api/auth/tenant` lookup the `:slug` addresses (US-1.1), not derived from
// the URL: a slug is an address, not a brand, and a monogram cut from it told the
// learner nothing about whose organisation they were signing in to. Plus the fi/en
// language toggle and the light/dark theme control. Every colour is a Verso token,
// so both themes come for free. Page-specific content is `children`.
export function AuthLayout({
  children,
  platformName,
}: {
  children: ReactNode;
  /**
   * The name shown in the branding slot when no tenant branding is available — the platform's own
   * identity. Defaults to the product's name; the operator console (apps/platform, ADR-0014 §6 phase 2)
   * passes its own, because its host addresses no tenant at all and "Verso People" would name the
   * product a platform operator is not signing in to.
   */
  platformName?: string;
}) {
  const { t } = useTranslation();
  const tenant = useTenantBranding();

  return (
    <main id="main" className="auth">
      {/* Decorative Verso platform brand — hidden on narrow screens. */}
      <aside className="auth__aside" aria-hidden="true">
        <div className="auth__aside-inner">
          <div className="auth__wordmark">
            <span className="auth__wordmark-mark">V</span>
            <span>Verso</span>
          </div>
          <p className="auth__tagline">{t("auth.tagline")}</p>
          <p className="auth__aside-foot">{t("auth.taglineSub")}</p>
        </div>
      </aside>

      <div className="auth__panel">
        <div className="auth__card">
          <TenantBrand tenant={tenant} platformName={platformName ?? t("app.title")} />

          {children}

          <div className="auth__footer">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </div>
      </div>
    </main>
  );
}

/**
 * The branding slot. While the lookup is in flight it is a placeholder rather than a guess, so the name
 * never flips from something wrong to something right in front of the learner. A tenant that is unknown
 * (or unreachable) falls back to the platform's own identity: this slot must not assert an organisation
 * the backend did not confirm — the page's own 404 state is what names the problem.
 */
function TenantBrand({
  tenant,
  platformName,
}: {
  tenant: TenantBrandingState;
  platformName: string;
}) {
  if (tenant.status === "loading") {
    return (
      <div className="auth__brand" data-testid="auth-brand" aria-busy="true">
        <span className="auth__brand-mark auth__brand-mark--pending" aria-hidden="true" />
        <span className="auth__brand-name auth__brand-name--pending" aria-hidden="true" />
      </div>
    );
  }

  const branding = tenant.status === "ready" ? tenant.branding : null;
  const name = branding?.name ?? platformName;

  return (
    <div className="auth__brand" data-testid="auth-brand">
      {branding?.logoUrl ? (
        // alt="" on purpose: the name sits right beside it, so an alt would be read twice.
        <img
          className="auth__brand-logo"
          data-testid="auth-brand-logo"
          src={branding.logoUrl}
          alt=""
        />
      ) : (
        <span className="auth__brand-mark" data-testid="auth-brand-mark" aria-hidden="true">
          {monogramOf(name)}
        </span>
      )}
      <span className="auth__brand-name">{name}</span>
    </div>
  );
}
